import { UserManager, WebStorageStateStore, type INavigator, type User } from "oidc-client-ts";
import { oidcConfig } from "../config/oidcConfig";

const storage = window.localStorage;

/**
 * Safari drops localStorage writes that happen in the same turn as a
 * cross-site navigation, and its pageshow event resolves the default
 * redirect promise before the IdP round trip finishes. Wait for the write
 * to flush, then leave the page without listening for pageshow.
 */
const SAFARI_STORAGE_FLUSH_MS = 400;

const redirectNavigator: INavigator = {
  async prepare() {
    return {
      async navigate(params) {
        await new Promise((resolve) => {
          window.setTimeout(resolve, SAFARI_STORAGE_FLUSH_MS);
        });
        window.location.assign(params.url);
        return new Promise(() => {
          // The page unloads on assign. Resolving here lets iOS resume the
          // login screen from the back-forward cache and start another redirect.
        });
      },
      close() {
        // Navigation is already underway.
      },
    };
  },
  async callback() {
    return undefined;
  },
};

const userManager = new UserManager(
  {
    ...oidcConfig,
    // Silent renew must not start during the login redirect. An iframe to
    // Keycloak on mobile races the PKCE state and bounces the page back here.
    automaticSilentRenew: false,
    userStore: new WebStorageStateStore({ store: storage }),
    stateStore: new WebStorageStateStore({ store: storage }),
    code_challenge_method: "S256",
    response_mode: "query",
  } as any,
  redirectNavigator,
);

userManager.events.addUserLoaded(() => {
  userManager.startSilentRenew();
});

userManager.events.addUserUnloaded(() => {
  userManager.stopSilentRenew();
});

let callbackTask: { url: string; promise: Promise<User | void> } | null = null;

const authService = {
  login: () =>
    userManager.signinRedirect({
      extraQueryParams: {
        kc_idp_hint: "keycloak",
      },
    }),
  logout: () => userManager.signoutRedirect(),
  getUser: async () => {
    const user = await userManager.getUser();
    if (user && user.expired !== true) {
      userManager.startSilentRenew();
    }
    return user;
  },
  signinSilent: () => userManager.signinSilent(),
  removeUser: () => userManager.removeUser(),
  handleCallback: () => {
    const url = window.location.href;
    if (!callbackTask || callbackTask.url !== url) {
      callbackTask = {
        url,
        promise: userManager.signinCallback(url),
      };
    }
    return callbackTask.promise;
  },
};

export default authService;
